import { initializeApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  User,
} from "firebase/auth";
import {
  getFirestore,
  connectFirestoreEmulator,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Auth
export const auth = getAuth(app);

// Initialize Firestore
export const db = getFirestore(app);

// Connect to emulators in development (disabled for now)
// if (import.meta.env.DEV && !auth.emulatorConfig) {
//   try {
//     connectAuthEmulator(auth, "http://localhost:9099");
//     connectFirestoreEmulator(db, "localhost", 8080);
//   } catch (error) {
//     console.warn("Firebase emulators may already be connected or unavailable:", error);
//   }
// }

// Auth helper functions
export const authService = {
  async signIn(email: string, password: string) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );
      return userCredential.user;
    } catch (error: any) {
      console.error("Sign in error:", error);

      // Create a more user-friendly error message
      let message = "Invalid email or password. Please try again.";

      if (error.code) {
        switch (error.code) {
          case "auth/user-not-found":
            message = "No account found with this email address.";
            break;
          case "auth/wrong-password":
            message = "Incorrect password. Please try again.";
            break;
          case "auth/invalid-credential":
            message = "Invalid email or password. Please try again.";
            break;
          case "auth/invalid-email":
            message = "Please enter a valid email address.";
            break;
          case "auth/user-disabled":
            message = "This account has been disabled.";
            break;
          case "auth/too-many-requests":
            message = "Too many failed attempts. Please try again later.";
            break;
          case "auth/network-request-failed":
            message =
              "Network error. Please check your connection and try again.";
            break;
          default:
            message = "Invalid email or password. Please try again.";
        }
      }

      // Create a new error with the user-friendly message
      const userError = new Error(message);
      throw userError;
    }
  },

  async signUp(email: string, password: string, displayName?: string, role?: string) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }

      // Create user document in Firestore
      await setDoc(doc(db, "users", userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: displayName || userCredential.user.displayName,
        createdAt: new Date().toISOString(),
        tenantId: getTenantIdFromEmail(email),
        role: role || "user",
        permissions: [],
      });

      return userCredential.user;
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  },

  async signOut() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  },

  async resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  },

  async getIdToken(user: User) {
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error("Get ID token error:", error);
      throw error;
    }
  },
};

// Firestore helper functions
export const firestoreService = {
  async getUserData(uid: string) {
    try {
      const userDoc = await getDoc(doc(db, "users", uid));
      return userDoc.exists() ? userDoc.data() : null;
    } catch (error) {
      console.error("Get user data error:", error);
      throw error;
    }
  },

  async updateUserData(uid: string, data: Partial<any>) {
    try {
      await updateDoc(doc(db, "users", uid), data);
    } catch (error) {
      console.error("Update user data error:", error);
      throw error;
    }
  },

  async getUsersByRole(role: string) {
    try {
      const q = query(collection(db, "users"), where("role", "==", role));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Get users by role error:", error);
      throw error;
    }
  },
};

// Utility function to determine tenant ID from email
function getTenantIdFromEmail(email: string): string {
  const domain = email.split("@")[1];

  // Map domains to tenant IDs based on your configuration
  const domainToTenant: Record<string, string> = {
    "asepha.ai": "asepha",
    "example.com": "example",
    // Add more domain mappings as needed
  };

  return domainToTenant[domain] || "guest";
}

export default app;
